import {
    Box,
    FormControlLabel,
    FormGroup,
    Grid,
    Paper,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon/src/datetime';
import * as React from 'react';
import { shallowEqual } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { RequestStatus } from '../../../../sic-lib/enums/RequestStatus';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import { CalendarEvent, CalendarEventFormState } from '../../../../sic-lib/types/CalendarEvent';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import ButtonLoading from '../../../Shared/ButtonLoading';
import SelectUser from '../../../Shared/SelectUser/SelectUser';
import SelectCalendarVisibility from '../../SelectCalendarVisibility/SelectCalendarVisibility';
import SelectContactsCalendarEvent from '../../SelectContactsCalendarEvent/SelectContactsCalendarEvent';

const useStyles = makeStyles()(theme => ({
    form: {
        margin: theme.spacing(2),
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        //width: 200,
    },
    dateField: {
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        width: 130,
    },
    timeField: {
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        width: 110,
    },
    switch: {
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
    },
    dropdownField: {
        width: 100,
    },
    integration: {
        padding: theme.spacing(2),
    },
    txtError: {
        marginLeft: theme.spacing(4.5),
        marginTop: theme.spacing(0.5),
        color: theme.palette.error.main,
    },
}));

interface Props {
    onCloseHandler: () => void;
}

export default function EditCalendarEvent({ onCloseHandler }: Props) {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const appState = useAppSelector(state => {
        return {
            requestStatus: RequestStatus.none,
            originalEvent: {} as CalendarEvent, // TODO REFACTOR
        };
    }, shallowEqual);

    const [formState, formDispatch] = React.useReducer<GenericReducer<CalendarEventFormState>>(
        genericReducer,
        {
            /*
            inputs controlled by formState property should have name attribute matching that formState property
         */
            dtpStartDate: appState.originalEvent.startDate,
            dtpEndDate: appState.originalEvent.endDate,
            chkAllDay: appState.originalEvent.allDay,
            ddlGuestEmails: appState.originalEvent.guestEmails || [],
            userName: appState.originalEvent.owner,
            txtTitle: appState.originalEvent.title,
            txtDescription: appState.originalEvent.description,
            txtLocation: appState.originalEvent.location,
            ddlStatus: appState.originalEvent.status,
            repeats: appState.originalEvent.repeats,
            visibility: appState.originalEvent.visibility,
            chkGenerateMeetLink: !!appState.originalEvent?.google?.meetId || false,
            chkGenerateZoomLink: !!appState.originalEvent?.zoomLink || false,
            /*
            validation error keys should match the formState property whose controlled input created the error
         */
            formValidation: {},
            loading: false,
            error: false,
            addContactAsGuest: true,
        },
    );

    const handleInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        let fieldValue: string | boolean | Date;

        switch (event.target.type) {
            case 'checkbox':
                fieldValue = event.target.checked;
                break;
            case 'date':
                console.log(event.target.value);
                fieldValue = event.target.value;
                break;
            default:
                fieldValue = event.target.value;
        }

        formDispatch({ [event.target.name]: fieldValue });
    };

    //for some reason the event from material ui date pickers dont have the event itself to resolve its 'name"
    const handleStartDateUpdate = (date: DateTime | null) => {
        //cant clean the start date
        if (date === null) return;

        if (formState.dtpEndDate !== null && date! >= formState.dtpEndDate!) {
            if (date.equals(formState.dtpEndDate)) {
                formDispatch({
                    ['dtpEndDate']: date.plus({ minutes: 15 }),
                });
            } else {
                formDispatch({
                    ['dtpEndDate']: date.plus(
                        formState.dtpEndDate
                            ?.diff(formState.dtpStartDate, ['days', 'hours', 'minutes'])
                            .toObject(),
                    ),
                });
            }
        }
        formDispatch({
            ['dtpStartDate']: date,
        });
    };

    const handleEndDateUpdate = (date: DateTime | null) => {
        formDispatch({
            ['dtpEndDate']: date,
        });
    };

    const handleSubmit = () => {
        try {
            if (formState.dtpStartDate >= formState.dtpEndDate!) {
                return formDispatch({
                    formValidation: {
                        ['dtpEndDate']: `Start Date cannot be grater than the End Date.`,
                    },
                });
            }
            // dispatch(
            //     calendarActions.updateCalendarEvent(
            //         appState.originalEvent.eventId,
            //         mapCalendarEventFormStateRequest(appState.originalEvent.contactId, formState),
            //     ),
            // );

            onCloseHandler();
        } catch (err: any) {
            formDispatch({ formValidation: { [err.field]: err.message } });
        }
    };

    return (
        <div className={classes.form}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        name="txtTitle"
                        label="Title"
                        error={formState.formValidation.txtTitle ? true : false}
                        helperText={formState.formValidation.txtTitle || ''}
                        value={formState.txtTitle}
                        onChange={handleInputChanged}
                        autoFocus
                        required
                        fullWidth
                    />
                </Grid>
                <Grid container item xs={12} alignItems="baseline">
                    <DatePicker
                        slotProps={{
                            textField: {
                                name: 'dtpStartDate',
                            },
                        }}
                        value={formState.dtpStartDate}
                        label="Start Date"
                        format="DD"
                        onChange={handleStartDateUpdate}
                        className={classes.dateField}
                    />
                    {!formState.chkAllDay && (
                        <TimePicker
                            slotProps={{
                                textField: {
                                    name: 'dtpStartDate',
                                },
                            }}
                            value={formState.dtpStartDate}
                            label="Start Time"
                            format="t"
                            onChange={handleStartDateUpdate}
                            className={classes.timeField}
                        />
                    )}
                    <Box component="div">to</Box>
                    {!formState.chkAllDay && (
                        <TimePicker
                            slotProps={{
                                textField: {
                                    name: 'dtpEndDate',
                                },
                            }}
                            value={formState.dtpEndDate}
                            label="End Time"
                            format="t"
                            onChange={handleEndDateUpdate}
                            className={classes.timeField}
                        />
                    )}
                    <DatePicker
                        slotProps={{
                            textField: {
                                name: 'dtpStartDate',
                            },
                        }}
                        value={formState.dtpEndDate}
                        minDate={formState.dtpStartDate}
                        label="End Date"
                        format="DD"
                        onChange={handleEndDateUpdate}
                        className={classes.dateField}
                    />
                    <FormGroup className={classes.switch} row={true}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formState.chkAllDay}
                                    onChange={handleInputChanged}
                                    name="chkAllDay"
                                    color="primary"
                                />
                            }
                            label="All Day"
                        />
                    </FormGroup>
                </Grid>
                <Typography variant="caption" className={classes.txtError}>
                    {formState.formValidation.dtpEndDate || ''}
                </Typography>
                {/*<Grid item xs={12} sm={6}>*/}
                {/*    <SelectCalenarEventRepeat*/}
                {/*        fullWidth*/}
                {/*        required*/}
                {/*        value={formState.repeats}*/}
                {/*        variant='outlined'*/}
                {/*        onSelectHandler={formDispatch}*/}
                {/*    />*/}
                {/*</Grid>*/}
                <Grid container item spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <SelectUser
                            fullWidth
                            required
                            label="Owner"
                            value={formState.userName || ''}
                            onSelectHandler={formDispatch}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SelectCalendarVisibility
                            fullWidth
                            required
                            value={formState.visibility}
                            onSelectHandler={formDispatch}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <SelectContactsCalendarEvent
                        selectedOwner={formState.userName}
                        guestList={formState.ddlGuestEmails}
                        validationError={formState.formValidation}
                        onSelectGuest={formDispatch}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormGroup>
                        <TextField
                            fullWidth
                            name="txtDescription"
                            label="Description"
                            multiline
                            rows={4}
                            value={formState.txtDescription}
                            onChange={handleInputChanged}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={12}>
                    <FormGroup>
                        <TextField
                            fullWidth
                            name="txtLocation"
                            label="Location"
                            value={formState.txtLocation}
                            onChange={handleInputChanged}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6}>
                    <Paper elevation={2} className={classes.integration}>
                        <FormGroup className={classes.switch} row={true}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formState.chkGenerateMeetLink}
                                        onChange={handleInputChanged}
                                        name="chkGenerateMeetLink"
                                        color="primary"
                                    />
                                }
                                label="Create a Google Meet conference for this event."
                            />
                        </FormGroup>
                    </Paper>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h6">Integrations</Typography>
                </Grid>
                {/* <Grid item xs={6}>
                        <Paper elevation={2} className={classes.integration}>
                            <FormGroup className={classes.switch} row={true}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formState.chkGenerateMeetlink}
                                            onChange={handleInputChanged}
                                            name="chkGenerateMeetlink"
                                            color="primary"
                                        />
                                    }
                                    label="Create a Google Meet conference for this event."
                                />
                            </FormGroup>
                        </Paper>
                    </Grid>
                    <Grid item xs={6}>
                        <Paper elevation={2} className={classes.integration}>
                            <FormGroup className={classes.switch} row={true}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formState.chkGenerateZoomLink}
                                            onChange={handleInputChanged}
                                            name="chkGenerateZoomLink"
                                            color="primary"
                                        />
                                    }
                                    label="Create a Zoom meeting for this event."
                                />
                            </FormGroup>
                        </Paper>
                    </Grid> */}
                <Grid item xs={12}>
                    <ButtonLoading
                        isLoading={appState.requestStatus === RequestStatus.active}
                        onClick={handleSubmit}
                        color="secondary"
                    >
                        Save and Create
                    </ButtonLoading>
                </Grid>
            </Grid>
        </div>
    );
}
